import { useEffect, useState } from "react";
import { useDispatch, useSelector } from "react-redux";

import TabButton from "../../../Components/common/TabButton/TabButton";
import ReactTableWrapper from "../../../Components/common/ReactTableWrapper/ReactTableWrapper";
import SandboxVisitButton from "../../../Components/common/SandboxVisit/SandboxVisitButton";
import SandboxVisitConfirm from "../../../Components/common/SandboxVisit/SandboxVisitConfirm";
import {
  DetailActions,
  DetailField,
  DetailGrid,
  DetailPanel,
} from "../../../Components/common/DetailPanel/DetailPanel";

import axiosApi from "../../../utils/axios";
import { URL_REGEX } from "../../../utils/constants/regex";
import { phishBoxHeader } from "../../../utils/constants/tableHeaders";

import { RequestPhishBoxList, SetPhishBoxActiveTab } from "../../../store/actions/phishBoxActions";
import { addNotification } from "../../../store/actions/notifierActions";

const PAGE_SIZE = 10;
const TABLE_NAME = "phishBox";

const SubComponent = ({ row }) => {
  const dispatch = useDispatch();
  const description = row?.original?.description || "-";
  const isUnAttended = description.includes("controlled");
  const url = row?.original?.link;
  const [showVisitConfirm, setShowVisitConfirm] = useState(false);

  // An invalid URL is reported straight away; a valid one is only opened
  // after the user confirms it.
  const requestVisitHandler = () => {
    if (!URL_REGEX.test(url)) {
      dispatch(
        addNotification({
          message: "Not a valid url",
          options: { variant: "error" },
        }),
      );
      return;
    }

    setShowVisitConfirm(true);
  };

  const visitHandler = async () => {
    try {
      const response = await axiosApi.get(
        `/api/phishbox/url-rewrite?url=${encodeURIComponent(url)}`,
      );
      const rewritedUrl = response?.data?.url;
      if (rewritedUrl) {
        window.open(`/psh1/view?u=${rewritedUrl}`, "_blank");
        dispatch(RequestPhishBoxList());
      } else {
        dispatch(
          addNotification({
            message: "Failed to visit page",
            options: { variant: "error" },
          }),
        );
      }
    } catch (e) {
      console.log(e);
      dispatch(
        addNotification({
          message: "Unexpected error",
          options: { variant: "error" },
        }),
      );
    }
  };

  return (
    <DetailPanel>
      <DetailGrid columns={1}>
        <DetailField label="Link" mono>
          {url}
        </DetailField>
        <DetailField label="Analysis">{description}</DetailField>
      </DetailGrid>
      {isUnAttended ? (
        <DetailActions>
          <SandboxVisitButton onClick={requestVisitHandler} />
        </DetailActions>
      ) : null}
      <SandboxVisitConfirm
        url={url}
        show={showVisitConfirm}
        setShow={setShowVisitConfirm}
        onConfirm={visitHandler}
      />
    </DetailPanel>
  );
};

const PhishBoxList = () => {
  const dispatch = useDispatch();

  const list = useSelector((state) => state.phishBox.phishBoxList);
  const loading = useSelector((state) => state.phishBox.phishBoxListLoading);
  const activeTab = useSelector((state) => state.phishBox.phishBoxActiveTab);
  const isTabSwitching = useSelector((state) => state.phishBox.phishBoxTabSwitching);

  useEffect(() => {
    const fetchData = () => dispatch(RequestPhishBoxList());

    fetchData();

    const interval = setInterval(() => {
      fetchData();
    }, 20000);

    return () => {
      clearInterval(interval);
      localStorage.setItem(`${TABLE_NAME}Toggle`, JSON.stringify({ ids: [] }));
    };
  }, [dispatch, activeTab]);

  const refreshHandler = () => dispatch(RequestPhishBoxList());

  return (
    <div className="phishbox__list-container">
      <h2>Submissions</h2>
      <div className="phishbox__table border" style={{ width: "100%" }}>
        <div className="button-block">
          <TabButton
            title="Latest"
            onClick={() => dispatch(SetPhishBoxActiveTab("Latest"))}
            active={activeTab}
          />
          <TabButton
            title="Not Allowed"
            onClick={() => dispatch(SetPhishBoxActiveTab("Not Allowed"))}
            active={activeTab}
          />
          <TabButton
            title="Visited"
            onClick={() => dispatch(SetPhishBoxActiveTab("Visited"))}
            active={activeTab}
          />
        </div>
        <ReactTableWrapper
          data={list}
          isDataLoading={loading}
          tableName={TABLE_NAME}
          columns={phishBoxHeader}
          paginationButtons
          customPageSize={PAGE_SIZE}
          refreshConfig={{ show: true, onClickHandler: refreshHandler }}
          styles={{ table: isTabSwitching ? { opacity: "0.4" } : {} }}
          expandConfig={{
            use: true,
            expandOnRowClick: true,
            subComponent: SubComponent,
            syncToggle: true,
          }}
        />
      </div>
    </div>
  );
};

export default PhishBoxList;
